import { Link } from 'react-router-dom';
import { MAIN_PHONE_DISPLAY, MAIN_PHONE_TEL } from '../../constants/contact';
import { pageMeta } from '../../constants/pageMeta';
import { COMPANY_EMAIL, COMPANY_NAME, SITE_NAME } from '../../constants/site';
import PolicyPage, { ContactBlock, List, Section } from '../components/PolicyPage';

const META = pageMeta(
  'Cancellation & Refund Policy',
  `How changes, cancellations and refunds work for travel booked with ${SITE_NAME} (${COMPANY_NAME}).`,
  '/cancellation-refund-policy'
);

export default function CancellationRefundPage() {
  return (
    <PolicyPage
      title="Cancellation & Refund Policy"
      meta={META}
      intro={`Because ${SITE_NAME} books travel as an agent, whether you can change, cancel or get a refund depends mainly on the travel provider’s rules. This page explains how it works and what we charge.`}
    >
      <Section title="1. Provider rules come first">
        <p>
          Every airline ticket, hotel rate, car rental, cruise and package has its own conditions, set by the provider.
          We give you a summary of these conditions in your quote and confirmation. Where our summary and the
          provider’s rules differ, the provider’s rules apply.
        </p>
      </Section>

      <Section title="2. 24-hour cancellation for U.S. flights">
        <p>
          For flights to, from or within the United States booked at least seven days before departure, U.S.
          Department of Transportation rules generally let you cancel within 24 hours of booking without an airline
          penalty, unless the airline instead lets you hold the fare without payment. Contact us within that 24-hour
          window and we will process the cancellation and refund in full, including our service fee.
        </p>
      </Section>

      <Section title="3. Cancelling after 24 hours">
        <List
          items={[
            'Refundable fares and rates: refunded according to the provider’s rules, minus any provider penalty.',
            'Non-refundable fares and rates: usually no cash refund. Some providers offer a credit or voucher instead, with their own expiry date.',
            'Taxes and fees: some government taxes on unused tickets can be refunded even on non-refundable fares. We will request these for you.',
            'Our service fee: non-refundable once the booking is issued, except as described in sections 2 and 5.',
          ]}
        />
      </Section>

      <Section title="4. Changes">
        <p>
          Changes such as new dates or names are possible only where the provider allows them. You may pay a provider
          change fee, any difference in price, and our change fee as listed on the <Link to="/service-fees">Service Fees</Link>{' '}
          page. We will tell you the full cost and get your approval before making any change.
        </p>
      </Section>

      <Section title="5. When the provider cancels or changes your trip">
        <p>
          If a provider cancels your trip or makes a significant change (for example a major schedule change on a
          flight), you may be entitled to a refund or alternative under the provider’s rules and applicable law,
          including U.S. DOT refund rules. We will help you choose and request the option you prefer, and we refund
          our service fee if the provider cancels and you choose a refund instead of an alternative.
        </p>
      </Section>

      <Section title="6. How long refunds take">
        <p>
          We submit refund requests to the provider within two business days of your request. Card refunds from
          airlines are usually returned within 7 business days, and cash refunds within 20 days, but timing depends on
          the provider and your bank. Refunds go back to the original payment method.
        </p>
      </Section>

      <Section title="7. How to cancel or request a refund">
        <p>
          Call <a href={`tel:${MAIN_PHONE_TEL}`}>{MAIN_PHONE_DISPLAY}</a> or email{' '}
          <a href={`mailto:${COMPANY_EMAIL}`}>{COMPANY_EMAIL}</a> with your confirmation number and the travellers’
          names. Please do not simply skip a flight — on many fares a no-show cancels the rest of the trip and removes
          any refund value.
        </p>
      </Section>

      <Section title="8. Contact us">
        <ContactBlock />
      </Section>
    </PolicyPage>
  );
}
